import React, { useState } from 'react';
import { useOrderAlert } from '../contexts/OrderAlertContext';

const OrderAlertOverlay = () => {
  const { pendingOrders, activeItemsByTable, isRinging, acceptOrder, rejectOrder, actionLoading } = useOrderAlert();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Don't render if no pending orders
  if (!pendingOrders || pendingOrders.length === 0) return null;

  // Clamp index
  const idx = Math.min(currentIndex, pendingOrders.length - 1);
  const order = pendingOrders[idx];
  if (!order) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit'
    });
  };

  const orderTypeLabels = {
    'DINING': 'Dine In',
    'DINE_IN': 'Dine In',
    'ONLINE': 'Online',
    'TAKEAWAY': 'Takeaway',
    'DELIVERY': 'Delivery'
  };

  const getTableNumber = (o) => {
    if (!o) return '';
    if (o.tableNumber) return o.tableNumber;
    if (o.table_booking_id?.tableNumber) return o.table_booking_id.tableNumber;
    if (o.tableBookingId?.tableNumber) return o.tableBookingId.tableNumber;
    if (o.tableBookingId?.tableId?.tableNumber) return o.tableBookingId.tableId.tableNumber;
    return '';
  };

  const allItems = order.orderItems || [];
  const isDineIn = order.orderType === 'DINING' || order.orderType === 'DINE_IN';

  const orderTypeStyles = {
    'DINING':   { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', shadow: 'rgba(16, 185, 129, 0.4)', icon: 'bi-shop' },
    'DINE_IN':  { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', shadow: 'rgba(16, 185, 129, 0.4)', icon: 'bi-shop' },
    'TAKEAWAY': { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', shadow: 'rgba(245, 158, 11, 0.4)', icon: 'bi-bag-fill' },
    'DELIVERY': { bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', shadow: 'rgba(59, 130, 246, 0.4)', icon: 'bi-scooter' },
    'ONLINE':   { bg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', shadow: 'rgba(139, 92, 246, 0.4)', icon: 'bi-globe' }
  };
  const typeStyle = orderTypeStyles[order.orderType] || orderTypeStyles['DINING'];
  const tableNumber = getTableNumber(order);

  // Repeat-order detection covers two backend flows:
  //  (1) New items appended to the same order entity → only the new items have
  //      status="PENDING" (old ones stay ACCEPTED/PREPARING/SERVED).
  //  (2) A brand-new order entity created for the same table while a previous
  //      order is still in progress → all items are PENDING here, but the
  //      table already has an active order in `activeItemsByTable`.
  const tableActiveItems =
    isDineIn && tableNumber && activeItemsByTable
      ? (activeItemsByTable[String(tableNumber)] || null)
      : null;
  const tableHasActiveOrder = tableActiveItems && tableActiveItems.size > 0;

  const pendingItems = allItems.filter(
    (item) => (item?.status || '').toUpperCase() === 'PENDING'
  );
  // Hide items that are already being prepared/served at this table (case 2).
  const itemsAfterTableFilter = tableHasActiveOrder
    ? allItems.filter((item) => !tableActiveItems.has(item?.menuItemName))
    : allItems;

  // Case 3 (fallback for Case 1 where the kitchen accept flow updates only
  // order-level status and leaves item.status stuck at "PENDING"): use
  // createdAt gaps to detect that some items were appended to the order
  // after an initial batch. A gap >30s between successive item createdAts
  // marks the boundary between the already-accepted batch and the new batch.
  const APPEND_GAP_MS = 30 * 1000;
  let timeNewBatch = null;
  if (isDineIn && allItems.length > 1 && allItems.every((it) => it?.createdAt)) {
    const sorted = [...allItems].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    let maxGap = 0;
    let gapIdx = -1;
    for (let i = 1; i < sorted.length; i++) {
      const gap =
        new Date(sorted[i].createdAt).getTime() -
        new Date(sorted[i - 1].createdAt).getTime();
      if (gap > maxGap) {
        maxGap = gap;
        gapIdx = i;
      }
    }
    if (maxGap > APPEND_GAP_MS && gapIdx > 0) {
      timeNewBatch = sorted.slice(gapIdx);
    }
  }

  // Case 0 (preferred): the backend's getOrdersWithFilters marks repeat orders
  // via OrderAlertMarker, setting `isRepeatOrder: true` and supplying
  // `previouslyAcceptedItemNames` (items already in progress at this table).
  const backendSaysRepeat = order?.isRepeatOrder === true;
  const backendPrevItems = Array.isArray(order?.previouslyAcceptedItemNames)
    ? new Set(order.previouslyAcceptedItemNames)
    : null;

  let hasRepeatItems = false;
  let displayItems = allItems;
  if (backendSaysRepeat) {
    hasRepeatItems = true;
    if (backendPrevItems && backendPrevItems.size > 0) {
      const filtered = allItems.filter((it) => !backendPrevItems.has(it?.menuItemName));
      displayItems = filtered.length > 0 ? filtered : allItems;
    }
  } else if (isDineIn && pendingItems.length > 0 && pendingItems.length < allItems.length) {
    // Case 1: same-order append (detected via item-level status)
    hasRepeatItems = true;
    displayItems = pendingItems;
  } else if (timeNewBatch && timeNewBatch.length > 0 && timeNewBatch.length < allItems.length) {
    // Case 3: same-order append detected via createdAt gap
    hasRepeatItems = true;
    displayItems = timeNewBatch;
  } else if (tableHasActiveOrder) {
    // Case 2: separate new order while table has an in-progress one
    hasRepeatItems = true;
    displayItems = itemsAfterTableFilter.length > 0 ? itemsAfterTableFilter : allItems;
  }
  const displayTotal = displayItems.reduce((sum, item) => {
    const itemTotal = Number(item?.itemTotal || 0);
    if (itemTotal > 0) return sum + itemTotal;
    const price = Number(item?.price || 0);
    const qty = Number(item?.quantity || 0);
    return sum + price * qty;
  }, 0);

  const handleAccept = () => {
    acceptOrder(order.id);
    if (idx >= pendingOrders.length - 1) setCurrentIndex(Math.max(0, idx - 1));
  };

  const handleReject = () => {
    rejectOrder(order.id);
    if (idx >= pendingOrders.length - 1) setCurrentIndex(Math.max(0, idx - 1));
  };

  const isLoading = actionLoading === order.id;

  return (
    <>
      {/* Backdrop */}
      <div style={styles.backdrop} />

      {/* Alert Card */}
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Bell Animation */}
          <div style={styles.bellWrapper}>
            <div style={{
              ...styles.bellCircle,
              animation: isRinging ? 'bellPulse 1.5s ease-in-out infinite' : 'none'
            }}>
              <i className="bi bi-bell-fill" style={{
                fontSize: '2rem',
                color: '#fff',
                animation: isRinging ? 'bellSwing 0.5s ease-in-out infinite alternate' : 'none'
              }}></i>
            </div>
            <div style={styles.bellLabel}>
              {hasRepeatItems ? 'Repeated Order' : 'New Order!'}
            </div>
            {isDineIn && tableNumber && (
              <div style={styles.tablePill}>
                Table {tableNumber}
              </div>
            )}
          </div>

          {/* Order Count Navigation */}
          {pendingOrders.length > 1 && (
            <div style={styles.orderNav}>
              <button
                onClick={() => setCurrentIndex(Math.max(0, idx - 1))}
                disabled={idx === 0}
                style={{
                  ...styles.navBtn,
                  opacity: idx === 0 ? 0.3 : 1
                }}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <span style={styles.orderCounter}>
                {idx + 1} of {pendingOrders.length} pending orders
              </span>
              <button
                onClick={() => setCurrentIndex(Math.min(pendingOrders.length - 1, idx + 1))}
                disabled={idx === pendingOrders.length - 1}
                style={{
                  ...styles.navBtn,
                  opacity: idx === pendingOrders.length - 1 ? 0.3 : 1
                }}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}

          {/* Order Info */}
          <div style={styles.orderInfo}>
            {/* Order Number & Type */}
            <div style={styles.orderHeader}>
              <div>
                <div style={styles.orderNumber}>{order.orderNumber}</div>
                <div style={styles.customerInline}>
                  <i className="bi bi-person-fill me-1"></i>
                  {order.customerName || 'Walk-in Customer'}
                  <span style={styles.inlineDivider}>·</span>
                  <i className="bi bi-clock me-1"></i>
                  {formatTime(order.createdAt)}
                </div>
              </div>
              <span style={{
                ...styles.orderTypeBadge,
                background: typeStyle.bg,
                color: '#fff',
                border: 'none',
                boxShadow: `0 4px 14px ${typeStyle.shadow}`,
                padding: '8px 16px',
                fontSize: '0.85rem',
                fontWeight: 800,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <i className={`bi ${typeStyle.icon}`}></i>
                {orderTypeLabels[order.orderType] || order.orderType}
              </span>
            </div>

            {/* Items List */}
            {displayItems && displayItems.length > 0 && (
              <div style={styles.itemsList}>
                <div style={styles.itemsScrollable}>
                  {displayItems.map((item, i) => {
                    const itemStatus = String(item?.status || '').toUpperCase();
                    const isCancelled = itemStatus === 'CANCELLED';
                    return (
                      <div key={item.id || i} style={styles.itemRow}>
                        <div style={{
                          ...styles.itemName,
                          textDecoration: isCancelled ? 'line-through' : 'none',
                          opacity: isCancelled ? 0.65 : 1
                        }}>
                          <span style={styles.itemQty}>{item.quantity}x</span>
                          {item.menuItemName}
                        </div>
                        {isCancelled && (
                          <span style={styles.cancelledTag}>
                            <i className="bi bi-x-circle-fill me-1"></i>Cancelled
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Delivery Address */}
            {order.deliveryAddress && (
              <div style={styles.addressRow}>
                <i className="bi bi-geo-alt me-1"></i>
                {order.deliveryAddress}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={styles.actions}>
            <button
              onClick={handleReject}
              disabled={isLoading}
              style={styles.rejectBtn}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
            >
              {isLoading && actionLoading === order.id ? (
                <span className="spinner-border spinner-border-sm" style={{ width: '16px', height: '16px' }}></span>
              ) : (
                <><i className="bi bi-x-lg me-2"></i>Reject</>
              )}
            </button>
            <button
              onClick={handleAccept}
              disabled={isLoading}
              style={styles.acceptBtn}
            >
              {isLoading && actionLoading === order.id ? (
                <span className="spinner-border spinner-border-sm" style={{ width: '16px', height: '16px' }}></span>
              ) : (
                <><i className="bi bi-check-lg me-2"></i>Accept Order</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes bellSwing {
          0% { transform: rotate(-15deg); }
          100% { transform: rotate(15deg); }
        }
        @keyframes bellPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5); }
          50% { box-shadow: 0 0 0 20px rgba(239, 68, 68, 0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeInBackdrop {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    zIndex: 10000,
    animation: 'fadeInBackdrop 0.3s ease'
  },
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
    padding: '20px',
    animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
  },
  card: {
    background: '#fff',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '440px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    flexDirection: 'column'
  },
  bellWrapper: {
    textAlign: 'center',
    padding: '28px 20px 16px',
    background: 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)'
  },
  bellCircle: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '10px'
  },
  bellLabel: {
    fontSize: '1.2rem',
    fontWeight: 800,
    color: '#dc2626',
    letterSpacing: '-0.02em'
  },
  tablePill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '8px',
    padding: '6px 14px',
    borderRadius: '999px',
    background: '#fee2e2',
    color: '#b91c1c',
    fontSize: '0.78rem',
    fontWeight: 800,
    border: '1px solid #fecaca'
  },
  orderNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '10px 20px',
    borderBottom: '1px solid #f1f5f9',
    background: '#fafbfc'
  },
  navBtn: {
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '0.85rem',
    color: '#475569'
  },
  orderCounter: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#64748b'
  },
  orderInfo: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px'
  },
  orderNumber: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#64748b',
    letterSpacing: 0
  },
  orderTime: {
    fontSize: '0.78rem',
    color: '#94a3b8',
    marginTop: '2px'
  },
  customerInline: {
    fontSize: '0.82rem',
    color: '#64748b',
    fontWeight: 400,
    marginTop: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  inlineDivider: {
    margin: '0 8px',
    color: '#cbd5e1'
  },
  orderTypeBadge: {
    padding: '5px 14px',
    borderRadius: '10px',
    fontSize: '0.78rem',
    fontWeight: 700,
    background: '#eff6ff',
    color: '#3b82f6',
    border: '1px solid #bfdbfe'
  },
  customerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 14px',
    background: '#f8fafc',
    borderRadius: '12px',
    marginBottom: '14px'
  },
  itemsList: {
    border: '2px solid #e2e8f0',
    borderRadius: '14px',
    overflow: 'hidden',
    marginBottom: '14px',
    background: '#fff',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)'
  },
  itemsHeader: {
    padding: '10px 14px',
    background: '#f8fafc',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#475569'
  },
  itemsScrollable: {
    maxHeight: '180px',
    overflowY: 'auto',
    padding: '4px 0'
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 14px',
    borderBottom: '1px solid #f8fafc'
  },
  itemName: {
    fontSize: '1rem',
    color: '#0f172a',
    fontWeight: 800,
    letterSpacing: '-0.01em',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  itemQty: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '28px',
    height: '26px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    color: '#92400e',
    fontSize: '0.8rem',
    fontWeight: 800,
    border: '1px solid #fcd34d'
  },
  itemPrice: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#475569',
    whiteSpace: 'nowrap'
  },
  cancelledTag: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: '999px',
    background: '#fef2f2',
    color: '#dc2626',
    fontSize: '0.7rem',
    fontWeight: 700,
    border: '1px solid #fecaca',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
    borderRadius: '12px',
    marginBottom: '10px',
    border: '1px solid #bbf7d0'
  },
  totalAmount: {
    fontSize: '1.25rem',
    fontWeight: 800,
    color: '#059669'
  },
  paymentRow: {
    fontSize: '0.82rem',
    color: '#64748b',
    padding: '8px 0'
  },
  addressRow: {
    fontSize: '0.82rem',
    color: '#64748b',
    padding: '8px 14px',
    background: '#f8fafc',
    borderRadius: '8px',
    marginTop: '6px'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px 24px',
    borderTop: '1px solid #f1f5f9'
  },
  rejectBtn: {
    flex: '1',
    padding: '14px 20px',
    borderRadius: '14px',
    border: '2px solid #fecaca',
    background: '#fef2f2',
    color: '#dc2626',
    fontSize: '0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  acceptBtn: {
    flex: '2',
    padding: '14px 20px',
    borderRadius: '14px',
    border: 'none',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    fontSize: '0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

export default OrderAlertOverlay;
